import { FC, ReactNode } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import { FolderOpen } from '@phosphor-icons/react';
import { Typography } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';

const emptyStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

const contentStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:not(:last-child) {
    margin-bottom: 24px;
  }

  > * {
    opacity: 0.75;
  }

  > .body1 {
    margin-top: 0.5rem;
  }
`;

const borderStyle = (theme: Theme) => css`
  padding: 48px 0;
  border: 2px solid ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;
`;

export interface EmptyProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: JSX.Element;
  element?: keyof JSX.IntrinsicElements;
  border?: boolean;
  children?: ReactNode;
}

export const Empty: FC<EmptyProps> = ({
  title = 'Nothing here…',
  subtitle = 'This listing is currently empty. Try creating something!',
  icon = <FolderOpen />,
  element: Element = 'li',
  border = false,
  children,
  ...other
}) => {
  const theme = useTheme();
  return (
    <Element className="empty" css={[emptyStyle, border && borderStyle]} {...other}>
      <span css={contentStyle}>
        {icon && <icon.type size={32} color={theme.color.grey[100]} {...icon.props} />}
        <Typography variant="body1">{title}</Typography>
        {!!subtitle && <Typography variant="body2">{subtitle}</Typography>}
      </span>
      {children}
    </Element>
  );
};
